/**
 * Liquid Cooling CDU edit body for the descriptor engine (#997 Story 6.8, mirror
 * of `CoolingSystemEngineEditFormBody`).
 *
 * Winston's ADR (parity by identity): the engine renders the EXISTING bespoke
 * `LiquidCoolingCduEditFormContent` — the user-approved CDU editor (field set,
 * order, labels, placeholders, the type toggle, dropdowns, the per-CDU details
 * attachment carousel at `SECTION_IDS.LIQUID_COOLING_PROJECT`, AND the per-CDU
 * Notes card at `SECTION_IDS.LIQUID_COOLING_NOTES`) — bound to the
 * engine's edit-session state. The engine owns list/save/flush/attachment
 * lifecycle (the 6.1/6.2 fixes), the section owns its view.
 *
 * Two Liquid-Cooling-specific deltas vs the Cooling edit body:
 *
 * 1. **Shared field.** `lcLiquidToChipDesired` is a REQUEST-LEVEL shared field
 *    (`sharedFieldKeys: ['lcLiquidToChipDesired']`), rendered inside the CDU
 *    editor but written to the request, not the CDU row. The host threads
 *    `sharedRequestFields` (value bag) + `onSharedRequestFieldChange` (setter)
 *    through this closure — exactly the props the bespoke subsection passed
 *    (`LiquidCooling.tsx`); the engine render props don't carry them.
 * 2. **Per-CDU Notes card (#997 B1 / PD-2).** Both the details and Notes
 *    attachment cards are scoped per-CDU (`{base}.unit.{configurationId}`) inside
 *    the bespoke content, so this body passes the FULL `attachmentsBySection` /
 *    `selectedFiles` maps through untouched (the content indexes them by the
 *    scoped id). `showAdditionalInformation` gates the Notes card (the host
 *    always passes it true, mirror `LiquidCooling.tsx`).
 *
 * Binding contract (identical to Cooling):
 * - `formData` is CDU-shaped: the engine's editor field map is coerced back
 *   through `descriptorFieldMapToPatch`, plus identity (`name` ← `instanceName`,
 *   `status` ← `instanceStatus`) and the unit id (`id` ← `configurationId`, which
 *   the bespoke content uses to scope its per-CDU attachment card — 6.1).
 * - `updateField` routes identity keys to the engine identity setters and every
 *   data field to `setInstanceField` (undefined → '', boolean kept). Liquid
 *   Cooling has ZERO `number`-typed descriptor fields, so nothing false-dirties.
 */

import React, { useCallback, useMemo } from 'react';

import { LiquidCoolingCduEditFormContent } from '../../../../components/liquidcoolingcdus/LiquidCoolingCduEditFormContent';
import { normalizeLiquidCoolingCduStatus } from '../../../../components/liquidcoolingcdus/liquidCoolingCduListUtils';
import { descriptorFieldMapToPatch } from '../../../sections/descriptorEngineAdapter';
import { LIQUID_COOLING_DESCRIPTOR } from '../../../sections/liquidCooling.descriptor';

import type { RepeatableInstanceEditFormRenderProps } from '../../../../components/repeatableSections/repeatableInstanceEditFormTypes';
import type { LiquidCoolingCduConfiguration } from '../../../../types/liquidCoolingCdu';

export type LiquidCoolingCduEngineEditFormBodyProps =
  RepeatableInstanceEditFormRenderProps & {
    /** Liquid Cooling gates its flat Notes card (mirror Cooling/Large UPS). */
    showAdditionalInformation?: boolean;
    showFullDetails?: boolean;
    /** Request-level shared field bag — carries `lcLiquidToChipDesired`. */
    sharedRequestFields?: Record<string, unknown>;
    /** Request-level shared field setter (routes to updateFormData/updateEditedData). */
    onSharedRequestFieldChange?: (
      field: string,
      value: string | boolean,
    ) => void;
  };

export function LiquidCoolingCduEngineEditFormBody({
  instanceFields,
  setInstanceField,
  instanceName,
  instanceStatus,
  setInstanceName,
  setInstanceStatus,
  disabled,
  requestStage,
  requestId,
  configurationId,
  attachmentsBySection,
  onAttachFiles,
  onDeleteAttachment,
  selectedFiles,
  onFilesSelected,
  offlineMode,
  onCaptionChange,
  pendingCaptionEdits,
  markDirty,
  showAdditionalInformation,
  showFullDetails,
  sharedRequestFields,
  onSharedRequestFieldChange,
}: LiquidCoolingCduEngineEditFormBodyProps): React.ReactElement {
  const toPatch = useMemo(
    () => descriptorFieldMapToPatch(LIQUID_COOLING_DESCRIPTOR),
    [],
  );

  const formData = useMemo((): Partial<LiquidCoolingCduConfiguration> => {
    const {
      instanceName: patchName,
      instanceStatus: patchStatus,
      ...dataFields
    } = toPatch(instanceFields);
    return {
      ...(dataFields as Partial<LiquidCoolingCduConfiguration>),
      id: configurationId,
      name: instanceName ?? (patchName as string | undefined),
      status: normalizeLiquidCoolingCduStatus(
        instanceStatus ?? (patchStatus as string | undefined),
      ),
    };
  }, [toPatch, instanceFields, configurationId, instanceName, instanceStatus]);

  const updateField = useCallback(
    <K extends keyof LiquidCoolingCduConfiguration>(
      field: K,
      value: LiquidCoolingCduConfiguration[K],
    ) => {
      if (field === 'name') {
        const name = typeof value === 'string' ? value : '';
        if (setInstanceName) {
          setInstanceName(name);
        } else {
          setInstanceField('instanceName', name);
        }
        return;
      }
      if (field === 'status') {
        const status = normalizeLiquidCoolingCduStatus(
          typeof value === 'string' ? value : undefined,
        );
        if (setInstanceStatus) {
          setInstanceStatus(status);
        } else {
          setInstanceField('instanceStatus', status);
        }
        return;
      }
      // The engine session stores editor forms: booleans stay booleans (yesNo),
      // a cleared value becomes blank — never 'undefined'. Liquid Cooling has no
      // number-typed field, so every value passes through as-is.
      const editorValue =
        typeof value === 'boolean' ? value : value == null ? '' : String(value);
      setInstanceField(field as string, editorValue);
    },
    [setInstanceField, setInstanceName, setInstanceStatus],
  );

  const onFilesSelectedDirty = useMemo(
    () =>
      onFilesSelected
        ? (sectionId: string, files: Parameters<typeof onFilesSelected>[1]) => {
            markDirty();
            onFilesSelected(sectionId, files);
          }
        : undefined,
    [markDirty, onFilesSelected],
  );

  return (
    <LiquidCoolingCduEditFormContent
      formData={formData}
      updateField={updateField}
      disabled={disabled}
      requestStage={requestStage}
      requestId={requestId}
      attachmentsBySection={attachmentsBySection}
      onAttachFiles={onAttachFiles}
      onDeleteAttachment={onDeleteAttachment}
      selectedFiles={selectedFiles}
      onFilesSelected={onFilesSelectedDirty}
      isOffline={offlineMode}
      onCaptionChange={onCaptionChange}
      pendingCaptionEdits={pendingCaptionEdits}
      showAdditionalInformation={showAdditionalInformation}
      showFullDetails={showFullDetails}
      sharedRequestFields={sharedRequestFields}
      onSharedRequestFieldChange={onSharedRequestFieldChange}
      onSharedFieldDirty={markDirty}
    />
  );
}
